import assertNever from "assert-never";
import React from "react";
import { Agency, AgencyId, TrainingCenterBundleDueDateId } from "../../../messages";
import { STRINGS } from "../../../strings";
import { TrainingCenterCaregiverBundle } from "../../../system/AppState/AppStateTrainingCenter";
import { translate } from "../../../translate";
import { HashMap } from "../../../utils/HashMap";
import { KeyedList } from "../../../utils/List";
import ScreenPlaceholder from "../../ScreenPlaceholder";
import { TrainingCenterLanguagePreference } from "../../TrainingCenter/TrainingCenterLanguageLookup";
import { isTrainingCenterBundleCompleted } from "../../TrainingCenter/TrainingCenterUtils";
import TrainingCenterBundlesCategory from "./TrainingCenterBundlesCategory";

export type TrainingCenterBundlesTab = "Required" | "Completed";

interface Props {
  activeTab: TrainingCenterBundlesTab;
  bundles: KeyedList<TrainingCenterCaregiverBundle, "bundleDueDateId">;
  agencies: HashMap<AgencyId, Agency>;
  languagePreference: TrainingCenterLanguagePreference;
  onClickBundle: (bundleDueDateId: TrainingCenterBundleDueDateId) => void;
}

const TRANSITION_DURATION = 500;

const TrainingCenterBundlesList = (props: Props) => {
  const [debouncedActiveTab, setDebouncedActiveTab] =
    React.useState<TrainingCenterBundlesTab | null>(null);

  const isInitialMount = React.useRef(true);
  const completedBundles = props.bundles.filter(isTrainingCenterBundleCompleted);
  const requiredBundles = props.bundles.filter((x) => !isTrainingCenterBundleCompleted(x));

  React.useEffect(() => {
    if (debouncedActiveTab === props.activeTab) {
      return;
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
      setDebouncedActiveTab(props.activeTab);
      return;
    }

    setDebouncedActiveTab(null);

    const timeout = setTimeout(() => setDebouncedActiveTab(props.activeTab), TRANSITION_DURATION);

    return () => clearTimeout(timeout);
  }, [debouncedActiveTab, props.activeTab]);

  const viewMode = React.useMemo(() => {
    if (props.activeTab === "Completed" && completedBundles.length() === 0) {
      return "EmptyCompleted" as const;
    }

    if (props.activeTab === "Required" && requiredBundles.length() === 0) {
      return "EmptyRequired" as const;
    }

    return "BundlesList" as const;
  }, [completedBundles, props.activeTab, requiredBundles]);

  switch (viewMode) {
    case "EmptyCompleted":
      return (
        <ScreenPlaceholder
          headline={translate(STRINGS.THERE_SEEMS_TO_BE_NOTHING_HERE)}
          illustration="NoCompletedInservices"
        />
      );
    case "EmptyRequired":
      return (
        <ScreenPlaceholder
          headline={translate(STRINGS.NO_LESSONS_FOR_YOU_YET)}
          illustration="NoAvailableInservices"
        />
      );
    case "BundlesList":
      return (
        <div className="TrainingCenterBundlesList">
          <TrainingCenterBundlesCategory
            isOpen={debouncedActiveTab === "Completed"}
            bundles={completedBundles}
            agencies={props.agencies}
            languagePreference={props.languagePreference}
            transitionDuration={TRANSITION_DURATION}
            onClickBundle={props.onClickBundle}
          />

          <TrainingCenterBundlesCategory
            isOpen={debouncedActiveTab === "Required"}
            bundles={requiredBundles}
            agencies={props.agencies}
            languagePreference={props.languagePreference}
            transitionDuration={TRANSITION_DURATION}
            onClickBundle={props.onClickBundle}
          />
        </div>
      );
    default:
      assertNever(viewMode);
  }
};

export default TrainingCenterBundlesList;
